import { useCallback, useRef, useState } from 'react';
import '../tailwind.css';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/Reporting/ui/card';
import ReportForm from '../../../components/Reporting/report-form';
import PosReports from '../../../components/Reporting/pos-reports';
import PosPerformanceOutput from '../../../components/Reporting/pos-performance-output';
import { HeaderMenu } from '../../../components/Reporting/header-menu';
import { ReportLoading } from '../../../components/Reporting/report-loading';
import instance from '../../../services/api';
import { toast } from 'react-toastify';
import PagePrintPreview from 'components/Reporting/page-print-preview';
import ThermalPrintView from 'components/Reporting/thermal-print-view';
import { Store } from 'lucide-react';
import useDetailedOrdersPager from './useDetailedOrdersPager';

// The detailed order list is served a page at a time; everything else in the
// report comes back whole.
const DETAILED_ORDERS_PAGE_SIZE = 25;

export default function PosReportsPage() {
  const [reportData, setReportData] = useState(null);
  const [include, setInclude] = useState(undefined);
  const [tenderTypes, setTenderTypes] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [show80mmView, setShow80mmView] = useState(false);
  const [showPagePreview, setShowPagePreview] = useState(false);
  // The form values behind the report on screen, so paging and the CSV export
  // can ask for more of the same range. A ref rather than state because the
  // prefetch that starts during handleFormSubmit has to see the new values
  // before React has had a chance to re-render.
  const reportRequestRef = useRef(null);

  const fetchOrdersPage = useCallback(async (page, pageSize) => {
    const response = await instance.post('/reports/pos-performance', {
      ...reportRequestRef.current,
      include: ['detailedReport'],
      includeTransaction: false,
      page,
      pageSize,
    });
    return response.data?.detailedReport;
  }, []);

  const handleOrdersPageReady = useCallback((detailedReport) => {
    setReportData((previous) => ({ ...previous, detailedReport }));
  }, []);

  const handleOrdersPageError = useCallback((error) => {
    toast.error(
      error.response?.data?.message ||
        'Failed to load that page of orders. Please try again.'
    );
  }, []);

  const {
    primePage: primeOrdersPage,
    goToPage: handleOrdersPageChange,
    reset: resetOrdersPager,
    isPageLoading: isOrdersPageLoading,
  } = useDetailedOrdersPager({
    fetchPage: fetchOrdersPage,
    onPageReady: handleOrdersPageReady,
    onError: handleOrdersPageError,
  });

  const handlePrint = () => {
    setShowPagePreview(true);
  };

  const handleFormSubmit = async (data) => {
    setIsLoading(true);
    // Bring the placeholder into view so the wait is visible from the form.
    setTimeout(() => {
      document.getElementById('pos-reports-loading')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 50);
    try {
      const response = await instance.post('/reports/pos-performance', {
        ...data,
        // Detail lists the report UI never renders. Asking for them costs
        // megabytes of payload for nothing.
        includeTransaction: false,
        includeProfitOrders: false,
        includeItemsWithoutCost: false,
        page: 0,
        pageSize: DETAILED_ORDERS_PAGE_SIZE,
      });
      setInclude(data.include);
      setTenderTypes(data.tenderTypes);
      reportRequestRef.current = data;
      setReportData(response.data);
      // Page 0 arrived with the report; the pages behind it load quietly.
      resetOrdersPager();
      primeOrdersPage(
        0,
        DETAILED_ORDERS_PAGE_SIZE,
        response.data?.detailedReport
      );
      toast.success('POS Report Generated Successfully');

      // Scroll to report output
      setTimeout(() => {
        document.getElementById('pos-reports')?.scrollIntoView({
          behavior: 'smooth',
        });
      }, 100);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        'Failed to generate POS reports. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Sending no page parameters returns every order in the range, which is what
  // the CSV export needs. Returns null when the request failed, so the export
  // knows to stop.
  const handleExportAllOrders = async () => {
    try {
      const response = await instance.post('/reports/pos-performance', {
        ...reportRequestRef.current,
        include: ['detailedReport'],
        includeTransaction: false,
      });
      return response.data?.detailedReport?.orders || [];
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        'Failed to export orders. Please try again.';
      toast.error(errorMessage);
      return null;
    }
  };

  const handle80mmPrint = () => {
    setShow80mmView(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <HeaderMenu
        currentPage="pos"
        reportData={reportData}
        onPrint={handlePrint}
        on80mmPrint={handle80mmPrint}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Report Form */}
        <Card className="no-print mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Store className="text-primary mr-3 h-6 w-6" />
              Generate POS Terminal Reports
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ReportForm
              onSubmit={handleFormSubmit}
              isLoading={isLoading}
              showPaymentMethods
              excludedIncludeSections={['driverBreakdown']}
            />
          </CardContent>
        </Card>

        {/* POS Reports Output */}
        {isLoading ? (
          <div id="pos-reports-loading">
            <ReportLoading />
          </div>
        ) : (
          reportData && (
            <div id="pos-reports" className="space-y-8">
              {/* Use PosPerformanceOutput for new API structure */}
              {reportData.todaysTotalOrders !== undefined ? (
                <PosPerformanceOutput
                  data={reportData}
                  include={include}
                  tenderTypes={tenderTypes}
                  onOrdersPageChange={handleOrdersPageChange}
                  onExportAllOrders={handleExportAllOrders}
                  isOrdersPageLoading={isOrdersPageLoading}
                />
              ) : (
                <PosReports data={reportData} />
              )}
            </div>
          )
        )}
      </div>

      {/* 80mm Thermal Print View */}
      {show80mmView && reportData && (
        <ThermalPrintView
          data={reportData}
          tenderTypes={tenderTypes}
          onClose={() => setShow80mmView(false)}
        />
      )}

      {/* Full Page Print Preview */}
      {showPagePreview && reportData && (
        <PagePrintPreview
          data={reportData}
          contentSelector="#pos-reports"
          onClose={() => setShowPagePreview(false)}
          reportType="POS-Performance"
        />
      )}
    </div>
  );
}
